import { Button, Container, createStyles, Divider, Theme } from "@material-ui/core";
import BackIcon from "@material-ui/icons/ArrowBackIos";
import { makeStyles } from "@material-ui/styles";
import { PDFDownloadLink, PDFViewer, usePDF } from '@react-pdf/renderer';
import React, { FC, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link, RouteComponentProps } from "react-router-dom";
import { createSelector } from "reselect";
import { Evaluation } from "../../evaluation";
import { AppState, loadEvaluation } from "../../redux";
import LoadingIndicator from "./form/components/LoadingIndicator";
import { EvaluationDocument } from './form/EvaluationDocument';
import {isMobile} from 'react-device-detect';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      WebkitOverflowScrolling: "touch",
    },
    pdfView: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      WebkitOverflowScrolling: "touch",
    },
    mobilePdfView: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: 'center',
      alignContent: 'center',
      alignItems: 'center',
      zIndex: 0,
      WebkitOverflowScrolling: "touch",
    },
    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "relative",
      padding: theme.spacing(1, 3),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1),
      },
      backgroundColor: theme.palette.background.paper,
    },
    downloadButton: {
      color: 'white',
      textDecoration: 'none',
    },
  })
);


interface PDFRenderProps extends RouteComponentProps<{ evalId: string }> { }

// problem is that these evals aren't actually available on page refresh, since the evals are loaded into the redux state
// by the eval list itself

// so this should get the eval out of idb instead
const selectEval = createSelector(
  (state: AppState, props: any) => {
    const evl = state.evals.items.getIn([props.match.params.evalId, 'data'])
    return evl;
  },
  (evl: Evaluation) => evl
)

const selectForm = createSelector(
  function formTypeSelector(state: AppState, props: any) {
    const evl = state.evals.items.get(props.match.params.evalId);
    return evl?.formType ?? null;
  },
  function formVersionSelector(state: AppState, props: any) {
    const evl = state.evals.items.get(props.match.params.evalId);
    return evl?.formVersion ?? null;
  },
  function formMapSelector(state: AppState) {
    return state.forms.items;
  },
  function loadForm(formType, formVersion, forms) {
    if (formType === null || formVersion === null || forms === null) return null;

    const versions = forms[formType];
    if (versions === null) return null;
    return versions[formVersion] ?? null;
  }
);

const PDFRender: FC<PDFRenderProps> = props => {
  const activeEvalId = decodeURIComponent(props.match.params.evalId);
  const dispatch = useDispatch();
  const classes = useStyles(props);
  const evaluation = useSelector<AppState, any>(state => selectEval(state, props), shallowEqual);
  const form = useSelector<AppState, any>(state => selectForm(state, props), shallowEqual);

  const memoizedPDF = useMemo(function() {
    return <EvaluationDocument data={evaluation} schema={form}/>;
  }, [evaluation, form]);

  const [pdfInstance, setPdfInstance] = usePDF({ document: memoizedPDF });
  const [ready, setReady] = useState(false);
  
  useEffect(function loadEval() {
    dispatch(loadEvaluation(activeEvalId));
  }, [activeEvalId, props.history, dispatch]);

  useEffect(() => {
    if (form && evaluation && !pdfInstance.loading && pdfInstance.blob) { 
      setReady(true);
    }

    return () => {
      if (pdfInstance.url) {
        URL.revokeObjectURL(pdfInstance.url);
      }
    }
  }, [form, evaluation, pdfInstance]);

  const pdfButton = useMemo(() => {
    if (!memoizedPDF) {
      return (
        <Button
          variant="contained"
          color="secondary"
          disabled
        > 
          Loading document...
        </Button>
      );
    }

    return (
      <Button
          variant="contained"
          color="secondary"
          disabled = { !ready }
        > 
          <PDFDownloadLink 
            className={ classes.downloadButton } 
            document={ memoizedPDF } 
            fileName="NMPFEvaluation.pdf"
          >
            { !ready ? 'Loading document...' : 'Download' }
          </PDFDownloadLink>
        </Button>
    )
  }, [memoizedPDF, pdfInstance, classes, ready])

  const toolbar = (
    <div className={classes.toolbar}>
      <Link to={"/evals"}>
        <Button>
          <BackIcon />
          Back
        </Button>
      </Link>
      { pdfButton }
    </div>
  );

  if (isMobile) {
    return (
      <div className={classes.root}>
        <Container className={classes.mobilePdfView}>
          { pdfButton }
        </Container>
      </div>
    )
  } else {
    return (
      <div className={classes.root}>
        { toolbar }
        <Divider />
        <Container className={classes.pdfView}>
            { !ready && (
              <LoadingIndicator>
                Loading PDF
              </LoadingIndicator>
            )}
          { ready && (
            <PDFViewer className={classes.pdfView}>
              {memoizedPDF}
            </PDFViewer>
          )}
        </Container>
      </div>
    );
  }
};

export default PDFRender;
